import { create } from 'zustand';
import {
  PublicKey,
  Transaction,
  VersionedTransaction,
} from '@solana/web3.js';
import {
  type Adapter,
  WalletReadyState,
} from '@solana/wallet-adapter-base';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { BackpackWalletAdapter } from '@solana/wallet-adapter-backpack';

export interface WalletInfo {
  name: string;
  icon: string;
  readyState: WalletReadyState;
}

const STORAGE_KEY = 'avcd-solana:wallet';

function rememberWallet(name: string | null) {
  if (typeof window === 'undefined') return;
  try {
    if (name) window.localStorage.setItem(STORAGE_KEY, name);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode / disabled storage — best-effort */
  }
}

function recallWallet(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Coerce whatever a wallet returned from `signMessage` into a plain Uint8Array.
 * Handles every observed shape: Uint8Array, Buffer, `{ type: 'Buffer', data: [] }`,
 * `{ signature: ... }`, and array-likes.
 */
function toUint8Array(v: unknown): Uint8Array {
  if (v instanceof Uint8Array) return new Uint8Array(v);
  if (v instanceof ArrayBuffer) return new Uint8Array(v);
  if (Array.isArray(v)) return Uint8Array.from(v as number[]);
  if (v && typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    // Wrapped result `{ signature, publicKey }` — unwrap.
    if (obj.signature !== undefined && obj.signature !== v) {
      return toUint8Array(obj.signature);
    }
    // Buffer.toJSON() shape: `{ type: 'Buffer', data: [...] }`
    if (obj.type === 'Buffer' && Array.isArray(obj.data)) {
      return Uint8Array.from(obj.data as number[]);
    }
    // TypedArray-like with .buffer (e.g. Node Buffer that lost prototype across realms)
    if (obj.buffer instanceof ArrayBuffer && typeof obj.byteLength === 'number') {
      return new Uint8Array(
        obj.buffer,
        (obj.byteOffset as number | undefined) ?? 0,
        obj.byteLength as number,
      );
    }
    // Numeric-keyed plain object: `{0: 12, 1: 99, ..., length: 64}`
    if (typeof obj.length === 'number') {
      return Uint8Array.from(obj as unknown as ArrayLike<number>);
    }
  }
  throw new TypeError('cannot convert wallet output to Uint8Array: ' + typeof v);
}

let adapters: Adapter[] = [];
let activeAdapter: Adapter | null = null;
let listenersInstalled = false;

function buildAdapters(): Adapter[] {
  if (adapters.length > 0) return adapters;
  adapters = [
    new PhantomWalletAdapter(),
    new SolflareWalletAdapter(),
    new BackpackWalletAdapter(),
  ];
  return adapters;
}

function installListeners(onChange: () => void): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  for (const a of buildAdapters()) {
    a.on('readyStateChange', onChange);
  }
}

interface WalletState {
  pubkey: string | null;
  walletName: string | null;
  walletIcon: string | null;
  connecting: boolean;
  available: WalletInfo[];
  publicKey: () => PublicKey | null;
  isConnected: () => boolean;
  refreshAvailable: () => void;
  initDiscovery: () => void;
  connect: (walletName?: string) => Promise<void>;
  disconnect: () => Promise<void>;
  tryAutoConnect: () => Promise<void>;
  signMessage: (message: Uint8Array) => Promise<Uint8Array>;
  signTransaction: <T extends Transaction | VersionedTransaction>(tx: T) => Promise<T>;
}

export const useWalletStore = create<WalletState>((set, get) => ({
  pubkey: null,
  walletName: null,
  walletIcon: null,
  connecting: false,
  available: [],
  publicKey: () => {
    const pk = get().pubkey;
    return pk ? new PublicKey(pk) : null;
  },
  isConnected: () => get().pubkey !== null,
  refreshAvailable: () => {
    set({
      available: buildAdapters().map((a) => ({
        name: a.name,
        icon: a.icon,
        readyState: a.readyState,
      })),
    });
  },
  initDiscovery: () => {
    installListeners(() => get().refreshAvailable());
    get().refreshAvailable();
  },
  connect: async (walletName?: string) => {
    if (typeof window === 'undefined') throw new Error('SSR');
    if (get().connecting) return;
    set({ connecting: true });
    try {
      get().initDiscovery();
      const candidates = buildAdapters();
      const adapter = walletName
        ? candidates.find((a) => a.name === walletName)
        : candidates.find(
            (a) =>
              a.readyState === WalletReadyState.Installed ||
              a.readyState === WalletReadyState.Loadable,
          );
      if (!adapter) {
        throw new Error(
          walletName
            ? `Wallet "${walletName}" not found.`
            : 'No Solana wallet detected. Install Phantom, Solflare, or Backpack.',
        );
      }
      if (
        adapter.readyState !== WalletReadyState.Installed &&
        adapter.readyState !== WalletReadyState.Loadable
      ) {
        // Open the wallet's install/marketplace URL in a new tab.
        const url = (adapter as Adapter & { url?: string }).url;
        if (url) window.open(url, '_blank', 'noopener');
        throw new Error(`${adapter.name} is not installed.`);
      }
      await adapter.connect();
      if (!adapter.publicKey) throw new Error(`${adapter.name} returned no public key`);
      activeAdapter = adapter;
      set({
        pubkey: adapter.publicKey.toBase58(),
        walletName: adapter.name,
        walletIcon: adapter.icon,
      });
      rememberWallet(adapter.name);
      adapter.on('disconnect', () => {
        if (activeAdapter === adapter) {
          activeAdapter = null;
          set({ pubkey: null, walletName: null, walletIcon: null });
          // The wallet itself initiated the disconnect (e.g. user revoked
          // permission inside Phantom). Forget it so we don't keep retrying
          // on every page load.
          rememberWallet(null);
        }
      });
    } finally {
      set({ connecting: false });
    }
  },
  disconnect: async () => {
    try {
      if (activeAdapter) await activeAdapter.disconnect();
    } catch {
      /* ignore */
    }
    activeAdapter = null;
    set({ pubkey: null, walletName: null, walletIcon: null });
    rememberWallet(null);
  },
  /**
   * Restore the previous wallet session on page load. Silent — failures are
   * logged but not surfaced; the user can always click Connect Wallet.
   *
   * Wallet extensions inject asynchronously, so the adapter may not be
   * `Installed` yet at the moment this runs. We listen for `readyStateChange`
   * with a short timeout to give the extension a chance to register.
   */
  tryAutoConnect: async () => {
    if (typeof window === 'undefined') return;
    const s = get();
    if (s.isConnected() || s.connecting) return;
    const stored = recallWallet();
    if (!stored) return;

    s.initDiscovery();
    const candidates = buildAdapters();
    const adapter = candidates.find((a) => a.name === stored);
    if (!adapter) {
      rememberWallet(null);
      return;
    }

    const isReady = (state: WalletReadyState) =>
      state === WalletReadyState.Installed || state === WalletReadyState.Loadable;

    const attempt = async () => {
      try {
        await get().connect(stored);
      } catch (err) {
        console.warn('[wallet] auto-connect failed:', err);
      }
    };

    if (isReady(adapter.readyState)) {
      await attempt();
      return;
    }

    // Wait up to 4s for the wallet extension to register, then give up.
    await new Promise<void>((resolve) => {
      let settled = false;
      const onChange = (state: WalletReadyState) => {
        if (settled) return;
        if (isReady(state)) {
          settled = true;
          adapter.off('readyStateChange', onChange);
          attempt().finally(resolve);
        }
      };
      adapter.on('readyStateChange', onChange);
      setTimeout(() => {
        if (settled) return;
        settled = true;
        adapter.off('readyStateChange', onChange);
        resolve();
      }, 4000);
    });
  },
  signMessage: async (message: Uint8Array) => {
    if (!activeAdapter) throw new Error('wallet not connected');
    const a = activeAdapter as Adapter & {
      signMessage?: (msg: Uint8Array) => Promise<Uint8Array>;
    };
    if (!a.signMessage) throw new Error(`${activeAdapter.name} does not support signMessage`);
    const out = await a.signMessage(message);
    // Wallets return signatures in a frustrating variety of shapes once they
    // cross the content-script / postMessage boundary:
    //   - real Uint8Array (Phantom on most builds)
    //   - Node Buffer (Solflare desktop)
    //   - Buffer JSON shape `{ type: "Buffer", data: number[] }` (Phantom on
    //     some Chrome builds — gets there via JSON-serialised postMessage)
    //   - plain `{ signature: ... }` wrapper (Backpack older builds)
    // Plus there's a Vite "two Uint8Array realms" issue that makes
    // `instanceof Uint8Array` lie. Normalise here once for everyone.
    const sig = toUint8Array(out);
    if (sig.length !== 64) {
      throw new Error(
        `${activeAdapter.name}: expected 64-byte Ed25519 signature from signMessage, got ${sig.length} bytes`,
      );
    }
    return sig;
  },
  signTransaction: async <T extends Transaction | VersionedTransaction>(tx: T): Promise<T> => {
    if (!activeAdapter) throw new Error('wallet not connected');
    const a = activeAdapter as Adapter & {
      signTransaction?: <U extends Transaction | VersionedTransaction>(tx: U) => Promise<U>;
    };
    if (!a.signTransaction) {
      throw new Error(`${activeAdapter.name} does not support signTransaction`);
    }
    const signed = (await a.signTransaction(tx)) as unknown;

    // Happy path: the wallet returned a real, usable transaction object.
    if (signed && typeof (signed as { serialize?: unknown }).serialize === 'function') {
      return signed as T;
    }

    // Some wallets round-trip the transaction through postMessage / JSON, so
    // the object we get back has lost its class prototype — calling
    // `.serialize()` on it later throws `e.serialize is not a function`.
    // Recover by lifting the produced signature(s) onto our original `tx`,
    // which still carries a valid prototype in this realm.
    const produced = (signed as { signatures?: unknown })?.signatures;

    if (tx instanceof VersionedTransaction) {
      if (Array.isArray(produced) && produced.length > 0) {
        tx.signatures = produced.map((s) => toUint8Array(s));
      }
      return tx;
    }

    if (tx instanceof Transaction) {
      if (Array.isArray(produced)) {
        for (const entry of produced as Array<{ publicKey?: unknown; signature?: unknown }>) {
          if (!entry || entry.signature == null || entry.publicKey == null) continue;
          try {
            const pk =
              entry.publicKey instanceof PublicKey
                ? entry.publicKey
                : new PublicKey(entry.publicKey as never);
            tx.addSignature(pk, toUint8Array(entry.signature) as never);
          } catch {
            /* skip un-reconstructable signature entry */
          }
        }
      }
      return tx;
    }

    return tx;
  },
}));
